import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, KeyRound } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { FormDialog, Field, TextInput, ActionButton } from "./common";

const PASSWORD_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#";
const genPassword = () => Array.from(crypto.getRandomValues(new Uint8Array(12)), (n) => PASSWORD_ALPHABET[n % PASSWORD_ALPHABET.length]).join("");

export const PortalAccess = ({ requester, companyCode, open, onOpenChange, onDone }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setPassword(requester?.portal_enabled ? "" : genPassword());
  }, [open, requester?.portal_enabled]);

  if (!requester) return null;

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/requesters/${requester.id}/portal-access`, { password });
      toast.success(`Portal login enabled — username: ${r.data.login_email}. Share these credentials with the requestor.`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/requesters/${requester.id}/portal-access`);
      toast.success(`Portal access revoked for ${requester.name}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormDialog open={open} onOpenChange={(value) => { if (!busy) onOpenChange(value); }} title={`Requester Login — ${requester.name}`} testId="portal-access-dialog">
      <div className="space-y-4">
        <div className="bg-slate-900/60 border border-slate-700 rounded-md px-4 py-3">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">Username (email)</p>
          {requester.email ? (
            <p className="text-sm text-amber-400 font-mono" data-testid="portal-login-email">{requester.email}</p>
          ) : (
            <p className="text-sm text-red-400" data-testid="portal-no-email-warning">
              This requester has no email address — add one first (it becomes the requestor's username).
            </p>
          )}
        </div>
        <Field label="Password">
          <div className="flex gap-2">
            <TextInput testId="portal-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={requester.portal_enabled ? "Enter new password to reset" : ""} />
            <ActionButton testId="portal-generate-button" variant="secondary" onClick={() => setPassword(genPassword())} className="!px-3">
              <RefreshCw size={14} />
            </ActionButton>
          </div>
          <p data-testid="portal-password-help" className="text-xs text-slate-500 mt-2">Use at least 8 characters. Resetting the password signs out existing portal sessions.</p>
        </Field>
        <p data-testid="portal-company-code" className="text-xs break-all">Company ID: <span className="font-mono">{companyCode}</span></p>
        {(!requester.active || !requester.customer_id) && <p data-testid="portal-assignment-warning" role="alert" className="text-sm text-red-600">Activate this requester and assign a customer before enabling login.</p>}
        <p data-testid="portal-login-help" className="text-xs text-slate-500 leading-relaxed">
          Share the email, password and Company ID with this requester. They use the Requester tab to view all Rental and SWD tickets for {requester.customer_name || "their assigned company"}. SWD approvals remain available; Rental tickets are view-only. Changing their email or company, or making them inactive, revokes access until re-enabled.
        </p>
        <div className="flex gap-3">
          <ActionButton testId="portal-save-button" onClick={save} disabled={busy || !requester.email || !requester.active || !requester.customer_id || password.length < 8} className="flex-1 justify-center">
            <KeyRound size={16} /> {requester.portal_enabled ? "Reset Password" : "Enable Portal Login"}
          </ActionButton>
          {requester.portal_enabled && (
            <ActionButton testId="portal-revoke-button" variant="danger" onClick={revoke} disabled={busy}>
              Revoke
            </ActionButton>
          )}
        </div>
        {requester.portal_enabled && (
          <p className="text-[11px] text-emerald-400/80 font-mono uppercase tracking-wider" data-testid="portal-enabled-note">
            Portal login currently enabled
          </p>
        )}
      </div>
    </FormDialog>
  );
};
